import { IsoActionPanel } from '../prescriptions/IsoActionPanel'
import { TestChangeRequestPanel } from '../prescriptions/TestChangeRequestPanel'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMutation, usePaginatedQuery, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { Button } from '@/components/ui/button'
import { Loading } from '@/components/ui/loading'
import QRCode from 'react-qr-code'
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  QrCode,
  Send,
  ShieldAlert,
  Video,
  X,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useChatPanel } from './ChatContext'
import {
  CHAT_ATTACHMENT_ACCEPT,
  CHAT_ATTACHMENT_FORMATS_LABEL,
  MAX_CHAT_ATTACHMENTS,
  MAX_CHAT_TOTAL_SIZE,
  chatContentTypeForFile,
  isChatVideoFile,
  validateSingleChatFile,
} from '@/lib/chatAttachmentRules'

const PAGE_SIZE = 30
const BOTTOM_THRESHOLD = 80

type ChatAttachment = {
  _id: Id<'prescription_chat_attachments'>
  filename: string
  content_type: string
  file_size: number
}

type ChatMessageKind = 'human' | 'system' | 'iso_event'

function formatTime(ts: number) {
  return new Date(ts).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getConvexSiteUrl() {
  const configured = import.meta.env.VITE_CONVEX_SITE_URL?.trim()
  if (configured) return configured.replace(/\/$/, '')
  const deploymentUrl = import.meta.env.VITE_CONVEX_URL?.trim()
  if (!deploymentUrl) {
    throw new Error('Endpoint allegati non configurato')
  }
  return deploymentUrl.replace(/\.convex\.cloud\/?$/, '.convex.site')
}

function chatUploadError(code: string) {
  switch (code) {
    case 'file_too_large':
      return 'Il file supera il limite previsto.'
    case 'unsupported_file_type':
    case 'invalid_file':
      return `Formato non supportato. Usa ${CHAT_ATTACHMENT_FORMATS_LABEL}.`
    case 'invalid_filename':
      return 'Il nome del file non è valido.'
    case 'upload_in_progress':
      return 'Questo caricamento è già in corso. Attendi qualche secondo e riprova.'
    case 'invalid_or_expired':
      return 'La sessione di caricamento è scaduta. Riprova.'
    default:
      return 'Caricamento non completato. Controlla la connessione e riprova.'
  }
}

export function ChatThread() {
  const { selectedPrescriptionId, selectPrescription } = useChatPanel()
  const thread = useQuery(
    api.prescriptionChat.getChatThread,
    selectedPrescriptionId ? { prescription_id: selectedPrescriptionId } : 'skip'
  )
  const {
    results: newestFirstMessages,
    status: messagesStatus,
    loadMore,
  } = usePaginatedQuery(
    api.prescriptionChat.listMessagesPage,
    selectedPrescriptionId ? { prescription_id: selectedPrescriptionId } : 'skip',
    { initialNumItems: PAGE_SIZE }
  )

  const sendMessage = useMutation(api.prescriptionChat.sendMessage)
  const markChatRead = useMutation(api.prescriptionChat.markChatRead)
  const createDesktopUploadSession = useMutation(
    api.prescriptionChat.createDesktopUploadSession
  )
  const discardPendingChatAttachment = useMutation(
    api.prescriptionChat.discardPendingChatAttachment
  )
  const createQrUploadSession = useMutation(api.chatQrUploads.createUploadSession)
  const revokeQrUploadSession = useMutation(api.chatQrUploads.revokeUploadSession)

  const [draft, setDraft] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasNewMessages, setHasNewMessages] = useState(false)
  const [creatingQr, setCreatingQr] = useState(false)
  const [qrNow, setQrNow] = useState(() => Date.now())
  const [qrSession, setQrSession] = useState<{
    id: Id<'chat_qr_upload_sessions'>
    url: string
    expiresAt: number
  } | null>(null)

  const qrSessionStatus = useQuery(
    api.chatQrUploads.getSessionStatus,
    qrSession ? { session_id: qrSession.id } : 'skip'
  )

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const preserveScrollRef = useRef<{ messageId: string; offsetTop: number } | null>(null)
  const isNearBottomRef = useRef(true)
  const initialScrollDoneRef = useRef(false)
  const newestSeenRef = useRef<string | null>(null)
  const hasSeenNewestRef = useRef(false)
  const lastMarkedMessageRef = useRef<string | null>(null)
  const qrSessionRef = useRef(qrSession)

  useEffect(() => {
    qrSessionRef.current = qrSession
  }, [qrSession])

  useEffect(() => {
    if (!qrSession) return
    setQrNow(Date.now())
    const timer = window.setInterval(() => setQrNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [qrSession])

  useEffect(
    () => () => {
      const active = qrSessionRef.current
      if (active) {
        void revokeQrUploadSession({ session_id: active.id }).catch(() => undefined)
      }
    },
    [revokeQrUploadSession, selectedPrescriptionId]
  )

  const messages = useMemo(
    () => [...newestFirstMessages].reverse(),
    [newestFirstMessages]
  )
  const newestMessage = newestFirstMessages[0]

  useEffect(() => {
    setDraft('')
    setSelectedFiles([])
    setQrSession(null)
    setError(null)
    setHasNewMessages(false)
    if (fileInputRef.current) fileInputRef.current.value = ''

    preserveScrollRef.current = null
    isNearBottomRef.current = true
    initialScrollDoneRef.current = false
    newestSeenRef.current = null
    hasSeenNewestRef.current = false
    lastMarkedMessageRef.current = null
  }, [selectedPrescriptionId])

  const markThroughMessage = useCallback(
    (messageId: Id<'prescription_messages'>) => {
      if (!selectedPrescriptionId || lastMarkedMessageRef.current === messageId) return

      lastMarkedMessageRef.current = messageId
      void markChatRead({
        prescription_id: selectedPrescriptionId,
        through_message_id: messageId,
      }).catch(() => {
        if (lastMarkedMessageRef.current === messageId) {
          lastMarkedMessageRef.current = null
        }
      })
    },
    [markChatRead, selectedPrescriptionId]
  )

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const container = scrollContainerRef.current
    if (!container) return
    container.scrollTo({ top: container.scrollHeight, behavior })
    isNearBottomRef.current = true
    setHasNewMessages(false)
  }, [])

  useLayoutEffect(() => {
    const container = scrollContainerRef.current
    if (!container || messagesStatus === 'LoadingFirstPage') return

    const preserved = preserveScrollRef.current
    if (preserved && messagesStatus !== 'LoadingMore') {
      const anchor = container.querySelector<HTMLElement>(
        `[data-chat-message-id="${CSS.escape(preserved.messageId)}"]`
      )
      if (anchor) {
        const containerTop = container.getBoundingClientRect().top
        const nextOffset = anchor.getBoundingClientRect().top - containerTop
        container.scrollTop += nextOffset - preserved.offsetTop
      }
      preserveScrollRef.current = null
    }

    const newestId = newestMessage?._id ?? null
    if (!initialScrollDoneRef.current) {
      container.scrollTop = container.scrollHeight
      initialScrollDoneRef.current = true
      isNearBottomRef.current = true
      newestSeenRef.current = newestId
      hasSeenNewestRef.current = true
      setHasNewMessages(false)
      return
    }

    if (hasSeenNewestRef.current && newestId !== newestSeenRef.current) {
      newestSeenRef.current = newestId

      if (isNearBottomRef.current) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
        isNearBottomRef.current = true
        setHasNewMessages(false)
      } else if (newestId) {
        setHasNewMessages(true)
      }
    }
  }, [messages.length, messagesStatus, newestMessage?._id])

  useEffect(() => {
    if (
      thread &&
      initialScrollDoneRef.current &&
      isNearBottomRef.current &&
      newestMessage
    ) {
      markThroughMessage(newestMessage._id)
    }
  }, [markThroughMessage, newestMessage?._id, thread])

  if (!selectedPrescriptionId) return null

  if (thread === undefined || messagesStatus === 'LoadingFirstPage') {
    return (
      <div className="flex h-full flex-col">
        <Header title="Chat" onBack={() => selectPrescription(null)} />
        <div className="flex flex-1 items-center justify-center">
          <Loading text="Caricamento messaggi..." />
        </div>
      </div>
    )
  }

  const handleLoadOlder = () => {
    const container = scrollContainerRef.current
    if (container) {
      const containerTop = container.getBoundingClientRect().top
      const anchors = Array.from(
        container.querySelectorAll<HTMLElement>('[data-chat-message-id]')
      )
      const anchor = anchors.find(
        (element) => element.getBoundingClientRect().bottom >= containerTop
      )
      if (anchor?.dataset.chatMessageId) {
        preserveScrollRef.current = {
          messageId: anchor.dataset.chatMessageId,
          offsetTop: anchor.getBoundingClientRect().top - containerTop,
        }
      }
    }
    loadMore(PAGE_SIZE)
  }

  const handleScroll = () => {
    const container = scrollContainerRef.current
    if (!container) return

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight
    const isNearBottom = distanceFromBottom <= BOTTOM_THRESHOLD
    isNearBottomRef.current = isNearBottom

    if (isNearBottom) {
      setHasNewMessages(false)
      if (newestMessage) markThroughMessage(newestMessage._id)
    }
  }

  const handleFilesSelected = (files: FileList | null) => {
    if (!files?.length) return
    setError(null)

    const incoming = Array.from(files)
    const nextFiles = [...selectedFiles, ...incoming]

    if (nextFiles.length > MAX_CHAT_ATTACHMENTS) {
      setError(`Puoi allegare al massimo ${MAX_CHAT_ATTACHMENTS} file per messaggio.`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    const invalid = incoming
      .map((file) => ({ file, message: validateSingleChatFile(file) }))
      .find((candidate) => candidate.message)
    if (invalid) {
      setError(`${invalid.file.name || 'Il file'}: ${invalid.message}`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    const totalSize = nextFiles.reduce((sum, file) => sum + file.size, 0)
    if (totalSize > MAX_CHAT_TOTAL_SIZE) {
      setError('Gli allegati superano il limite complessivo di 40 MB per messaggio.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setSelectedFiles(nextFiles)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSend = async () => {
    const body = draft.trim()
    if ((!body && selectedFiles.length === 0) || sending) return

    setSending(true)
    setError(null)
    const registeredAttachmentIds: Id<'prescription_chat_attachments'>[] = []

    try {
      for (const file of selectedFiles) {
        const session = await createDesktopUploadSession({
          prescription_id: selectedPrescriptionId,
        })
        const uploadResponse = await fetch(`${getConvexSiteUrl()}/api/chat/desktop-upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.token}`,
            'Content-Type': chatContentTypeForFile(file),
            'X-File-Name': encodeURIComponent(file.name),
          },
          body: file,
        })
        const uploadResult = (await uploadResponse.json().catch(() => ({}))) as {
          attachment_id?: Id<'prescription_chat_attachments'>
          error?: string
        }
        if (!uploadResponse.ok || !uploadResult.attachment_id) {
          throw new Error(
            `${file.name}: ${chatUploadError(uploadResult.error ?? 'upload_failed')}`
          )
        }
        registeredAttachmentIds.push(uploadResult.attachment_id)
      }

      await sendMessage({
        prescription_id: selectedPrescriptionId,
        body,
        ...(registeredAttachmentIds.length > 0
          ? { attachment_ids: registeredAttachmentIds }
          : {}),
      })
      setDraft('')
      setSelectedFiles([])
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err: unknown) {
      if (registeredAttachmentIds.length > 0) {
        await Promise.allSettled(
          registeredAttachmentIds.map((attachmentId) =>
            discardPendingChatAttachment({ attachment_id: attachmentId })
          )
        )
      }
      setError(err instanceof Error ? err.message : 'Invio fallito')
    } finally {
      setSending(false)
    }
  }

  const handleOpenQr = async () => {
    if (creatingQr) return
    setCreatingQr(true)
    setError(null)
    try {
      const session = await createQrUploadSession({
        prescription_id: selectedPrescriptionId,
      })
      setQrSession({
        id: session.session_id,
        url: `${window.location.origin}/chat-upload#${session.token}`,
        expiresAt: session.expires_at,
      })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Impossibile generare il QR')
    } finally {
      setCreatingQr(false)
    }
  }

  const handleCloseQr = () => {
    const active = qrSession
    setQrSession(null)
    if (active && qrSessionStatus?.status !== 'consumed') {
      void revokeQrUploadSession({ session_id: active.id }).catch(() => undefined)
    }
  }

  const qrSecondsRemaining = qrSession
    ? Math.max(0, Math.ceil((qrSession.expiresAt - qrNow) / 1_000))
    : 0

  return (
    <div className="flex h-full flex-col bg-white">
      <Header
        title={thread.prescription.patient_name}
        subtitle={`n. ${thread.prescription.numero_prescrizione}`}
        onBack={() => selectPrescription(null)}
      />

      <div className="max-h-[45%] shrink-0 overflow-y-auto empty:hidden">
        <TestChangeRequestPanel key={selectedPrescriptionId} prescriptionId={selectedPrescriptionId}
          channel="chat" fromChat className="m-3" />
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="relative flex-1 space-y-3 overflow-y-auto px-3 py-3"
      >
        {messagesStatus === 'CanLoadMore' || messagesStatus === 'LoadingMore' ? (
          <div className="flex justify-center pb-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleLoadOlder}
              disabled={messagesStatus === 'LoadingMore'}
            >
              {messagesStatus === 'LoadingMore' && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Carica messaggi precedenti
            </Button>
          </div>
        ) : null}

        {messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            Nessun messaggio. Scrivi al laboratorio o alla clinica.
          </p>
        ) : (
          messages.map((message) => (
            <MessageBubble
              key={message._id}
              messageId={message._id}
              body={message.body}
              name={message.sender_name}
              time={message.created_at}
              side={message.sender_side}
              isMine={message.is_mine}
              kind={message.kind}
              isoReportId={message.iso_report_id}
              prescriptionId={message.prescription_id}
              isoActionKind={message.iso_action_kind}
              isoActionStatus={message.iso_action_status}
              isoSeverity={message.iso_severity_level}
              attachments={message.attachments}
            />
          ))
        )}
      </div>

      {hasNewMessages && (
        <button
          type="button"
          onClick={() => {
            scrollToBottom()
            if (newestMessage) markThroughMessage(newestMessage._id)
          }}
          className="mx-auto -mt-11 mb-2 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-white shadow-lg"
        >
          Nuovi messaggi
        </button>
      )}

      <div className="border-t bg-white p-3">
        {error && (
          <p role="alert" aria-live="polite" className="mb-2 text-xs text-red-600">
            {error}
          </p>
        )}

        {selectedFiles.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {selectedFiles.map((file, index) => (
              <div
                key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                className="flex max-w-full items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700"
              >
                {isChatVideoFile(file) ? (
                  <Video className="h-3 w-3 shrink-0" aria-hidden="true" />
                ) : (
                  <Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
                )}
                <span className="max-w-[190px] truncate">{file.name}</span>
                <span className="shrink-0 text-gray-400">{formatFileSize(file.size)}</span>
                <button
                  type="button"
                  className="ml-0.5 rounded-full p-0.5 hover:bg-gray-200"
                  onClick={() =>
                    setSelectedFiles((current) => current.filter((_, i) => i !== index))
                  }
                  disabled={sending}
                  aria-label={`Rimuovi ${file.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={CHAT_ATTACHMENT_ACCEPT}
          className="hidden"
          onChange={(event) => handleFilesSelected(event.target.files)}
          disabled={sending}
        />

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10 w-10 shrink-0 p-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || selectedFiles.length >= MAX_CHAT_ATTACHMENTS}
            aria-label="Allega foto, video o PDF"
            aria-describedby="chat-attachment-limits"
            title={`Allega ${CHAT_ATTACHMENT_FORMATS_LABEL}`}
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10 w-10 shrink-0 p-0"
            onClick={() => void handleOpenQr()}
            disabled={sending || creatingQr}
            aria-label="Allega dal telefono tramite QR"
            title="Allega dal telefono tramite QR"
          >
            {creatingQr ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <QrCode className="h-4 w-4" />
            )}
          </Button>
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                void handleSend()
              }
            }}
            rows={2}
            maxLength={4000}
            placeholder="Scrivi un messaggio..."
            className="min-h-[44px] flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/40"
            disabled={sending}
          />
          <Button
            onClick={() => void handleSend()}
            disabled={sending || (!draft.trim() && selectedFiles.length === 0)}
            className="self-end"
            size="sm"
            aria-label="Invia messaggio"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p id="chat-attachment-limits" className="mt-1.5 text-[11px] text-gray-500">
          Fino a 4 allegati. Foto e PDF fino a 10 MB; video MP4, WebM o MOV fino a
          19 MB. Totale 40 MB.
        </p>
      </div>

      {qrSession && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-qr-title"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 id="chat-qr-title" className="text-lg font-semibold text-gray-900">
                  Allega dal telefono
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Inquadra il QR, scatta una foto o scegli una foto, un video o un PDF.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseQr}
                className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
                aria-label="Chiudi QR"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {qrSessionStatus?.status === 'consumed' ? (
              <div className="flex flex-col items-center py-8 text-center">
                <CheckCircle2 className="mb-3 h-12 w-12 text-green-600" />
                <p className="font-medium text-gray-900">Allegato ricevuto</p>
                <p className="mt-1 text-sm text-gray-500">
                  Il file è già comparso nella chat.
                </p>
              </div>
            ) : qrSessionStatus?.status === 'expired' || qrSecondsRemaining <= 0 ? (
              <div className="rounded-lg bg-amber-50 px-4 py-6 text-center text-sm text-amber-800">
                Questo QR è scaduto. Chiudilo e generane uno nuovo.
              </div>
            ) : qrSessionStatus?.status === 'revoked' ||
              qrSessionStatus?.status === 'failed' ? (
              <div className="rounded-lg bg-red-50 px-4 py-6 text-center text-sm text-red-700">
                Questo QR non è più utilizzabile.
              </div>
            ) : (
              <>
                <div className="mx-auto w-fit rounded-xl border border-gray-200 bg-white p-4">
                  <QRCode value={qrSession.url} size={220} level="M" />
                </div>
                <p className="mt-3 text-center text-xs text-gray-500">
                  Scade tra {Math.floor(qrSecondsRemaining / 60)}:
                  {String(qrSecondsRemaining % 60).padStart(2, '0')}
                </p>
                {qrSessionStatus?.status === 'uploading' && (
                  <p className="mt-2 flex items-center justify-center gap-2 text-sm text-primary">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Caricamento dal telefono in corso…
                  </p>
                )}
              </>
            )}

            <Button type="button" variant="outline" className="mt-5 w-full" onClick={handleCloseQr}>
              Chiudi
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function Header({
  title,
  subtitle,
  onBack,
}: {
  title: string
  subtitle?: string
  onBack: () => void
}) {
  return (
    <div className="flex items-center gap-2 border-b px-3 py-3">
      <Button variant="outline" size="sm" onClick={onBack} aria-label="Indietro">
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <div className="min-w-0">
        <div className="truncate font-semibold text-gray-900">{title}</div>
        {subtitle ? <div className="truncate text-xs text-gray-500">{subtitle}</div> : null}
      </div>
    </div>
  )
}

function MessageBubble({
  messageId,
  body,
  name,
  time,
  side,
  isMine,
  kind,
  isoReportId,
  prescriptionId, isoActionKind, isoActionStatus, isoSeverity,
  attachments,
}: {
  messageId: Id<'prescription_messages'>
  body: string
  name: string
  time: number
  side: 'clinic' | 'lab'
  isMine: boolean
  kind: ChatMessageKind
  isoReportId?: Id<'iso_reports'>
  prescriptionId: Id<'prescriptions'>
  isoActionKind?: 'replan' | 'confirm'
  isoActionStatus?: string
  isoSeverity?: string
  attachments: ChatAttachment[]
}) {
  if (kind === 'iso_event') {
    return <IsoMessage messageId={messageId} body={body} time={time} prescriptionId={prescriptionId}
      actionKind={isoActionKind} actionStatus={isoActionStatus} severity={isoSeverity} />
  }

  return (
    <div
      data-chat-message-id={messageId}
      className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
          isMine
            ? 'bg-primary text-white'
            : side === 'lab'
              ? 'bg-slate-100 text-slate-900'
              : 'bg-emerald-50 text-emerald-950'
        }`}
      >
        {!isMine && (
          <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wide opacity-70">
            {name} · {side === 'lab' ? 'Lab' : 'Clinica'}
          </div>
        )}
        {body && <div className="whitespace-pre-wrap break-words">{body}</div>}
        {attachments.length > 0 && (
          <div className={`space-y-1.5 ${body ? 'mt-2' : ''}`}>
            {attachments.map((attachment) => (
              <MessageAttachment
                key={attachment._id}
                attachment={attachment}
                isMine={isMine}
              />
            ))}
          </div>
        )}
        <div className={`mt-1 text-[10px] ${isMine ? 'text-white/70' : 'text-gray-500'}`}>
          {formatTime(time)}
        </div>
      </div>
    </div>
  )
}

function IsoMessage({ messageId, body, time, prescriptionId, actionKind, actionStatus, severity }: {
  messageId: Id<'prescription_messages'>; body: string; time: number; prescriptionId: Id<'prescriptions'>
  actionKind?: 'replan' | 'confirm'; actionStatus?: string; severity?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const { closeChat } = useChatPanel()
  const red = severity === 'critical' || severity === 'high'
  const tone = red ? 'border-red-200 bg-red-50 text-red-950' : severity === 'low'
    ? 'border-green-200 bg-green-50 text-green-950' : 'border-amber-200 bg-amber-50 text-amber-950'
  return <div data-chat-message-id={messageId} className="flex justify-center py-1">
    <div className={`w-full min-w-0 max-w-[94%] rounded-xl border px-3 py-3 ${tone}`}>
      <p className="flex items-center gap-2 text-sm font-semibold"><ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
        {actionStatus === 'synced' ? 'Risposta ISO inviata a PrimoUp' : red ? 'ISO · intervento richiesto' : actionKind ? 'ISO · conferma richiesta' : 'Informazione ISO'}
      </p>
      <p className="mt-2 whitespace-pre-wrap break-words text-sm">{body}</p>
      {actionKind && actionStatus !== 'synced' && <Button type="button" size="sm" className="mt-3 h-auto min-h-10 whitespace-normal text-left" onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Chiudi richiesta' : actionStatus === 'syncing' ? 'Vedi stato invio' : actionStatus === 'failed' ? 'Vedi come riprovare' : 'Rispondi e aggiorna la prescrizione'}
      </Button>}
      {expanded && <div className="mt-3"><IsoActionPanel prescriptionId={prescriptionId} autoOpen fromChat onOpenPrescription={closeChat} /></div>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs">
        <span>{formatTime(time)}</span>
        <Link to={`/prescriptions/${prescriptionId}?iso=1#iso-action`} onClick={closeChat} className="font-medium underline underline-offset-2">Apri prescrizione</Link>
      </div>
    </div>
  </div>
}

function MessageAttachment({
  attachment,
  isMine,
}: {
  attachment: ChatAttachment
  isMine: boolean
}) {
  const createDownloadToken = useMutation(
    api.prescriptionChat.createChatAttachmentDownloadToken
  )
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle'
  )
  const objectUrlRef = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const loadingRef = useRef<Promise<string | null> | null>(null)
  const delayedRevocationsRef = useRef<Map<string, number>>(new Map())
  const isImage = attachment.content_type.startsWith('image/')
  const isVideo = attachment.content_type.startsWith('video/')
  const linkClass = isMine
    ? 'border-white/20 bg-white/10 text-white hover:bg-white/20'
    : 'border-gray-200 bg-white/80 text-gray-800 hover:bg-white'

  useEffect(
    () => () => {
      abortRef.current?.abort()
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
      for (const [objectUrl, timeoutId] of delayedRevocationsRef.current) {
        window.clearTimeout(timeoutId)
        URL.revokeObjectURL(objectUrl)
      }
      delayedRevocationsRef.current.clear()
    },
    []
  )

  const loadAttachment = useCallback(async (): Promise<string | null> => {
    if (objectUrlRef.current) return objectUrlRef.current
    if (loadingRef.current) return await loadingRef.current

    const request = (async () => {
      setLoadState('loading')
      const controller = new AbortController()
      abortRef.current = controller
      try {
        const capability = await createDownloadToken({
          attachment_id: attachment._id,
        })
        const response = await fetch(`${getConvexSiteUrl()}/api/chat/attachment`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${capability.token}` },
          cache: 'no-store',
          signal: controller.signal,
        })
        if (!response.ok) {
          throw new Error('Allegato non disponibile')
        }
        const blob = await response.blob()
        if (blob.size !== attachment.file_size) {
          throw new Error('Allegato incompleto')
        }
        const objectUrl = URL.createObjectURL(blob)
        if (controller.signal.aborted) {
          URL.revokeObjectURL(objectUrl)
          return null
        }
        objectUrlRef.current = objectUrl
        setLoadState('ready')
        return objectUrl
      } catch (error) {
        if (!(error instanceof Error && error.name === 'AbortError')) {
          setLoadState('error')
        }
        return null
      } finally {
        if (abortRef.current === controller) abortRef.current = null
      }
    })()
    loadingRef.current = request
    try {
      return await request
    } finally {
      loadingRef.current = null
    }
  }, [attachment._id, attachment.file_size, createDownloadToken])

  const downloadAttachment = useCallback(async () => {
    const objectUrl = await loadAttachment()
    if (!objectUrl) return
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = attachment.filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    if (!isImage && !isVideo && objectUrlRef.current === objectUrl) {
      objectUrlRef.current = null
      setLoadState('idle')
      const timeoutId = window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl)
        delayedRevocationsRef.current.delete(objectUrl)
      }, 60_000)
      delayedRevocationsRef.current.set(objectUrl, timeoutId)
    }
  }, [attachment.filename, isImage, isVideo, loadAttachment])

  const objectUrl = objectUrlRef.current
  const actionLabel = loadState === 'loading'
    ? 'Caricamento…'
    : loadState === 'error'
      ? 'Riprova'
      : isVideo
        ? 'Carica video'
        : isImage
          ? 'Visualizza foto'
          : 'Scarica allegato'

  if (!objectUrl) {
    const AttachmentIcon = isVideo ? Video : isImage ? ImageIcon : FileText
    return (
      <button
        type="button"
        onClick={() => void (isImage || isVideo ? loadAttachment() : downloadAttachment())}
        disabled={loadState === 'loading'}
        className={`flex w-full items-center gap-2 rounded-lg border px-2 py-2 text-left disabled:cursor-wait disabled:opacity-70 ${linkClass}`}
        aria-label={`${actionLabel}: ${attachment.filename}`}
      >
        {loadState === 'loading' ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
        ) : (
          <AttachmentIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium">{attachment.filename}</span>
          <span className="block text-[10px] opacity-70">
            {actionLabel} · {formatFileSize(attachment.file_size)}
          </span>
        </span>
      </button>
    )
  }

  if (isImage) {
    return (
      <div className={`overflow-hidden rounded-lg border ${linkClass}`}>
        <img
          src={objectUrl}
          alt={attachment.filename}
          className="max-h-44 w-full object-cover"
        />
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 text-[10px]">
          <span className="truncate">{attachment.filename}</span>
          <button
            type="button"
            onClick={() => void downloadAttachment()}
            className="flex shrink-0 items-center gap-1 rounded underline-offset-2 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-current"
          >
            {formatFileSize(attachment.file_size)}
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="sr-only">Scarica {attachment.filename}</span>
          </button>
        </div>
      </div>
    )
  }

  if (isVideo) {
    return (
      <div className={`overflow-hidden rounded-lg border ${linkClass}`}>
        <video
          controls
          playsInline
          preload="none"
          src={objectUrl}
          className="max-h-64 w-full bg-black object-contain"
          aria-label={`Video allegato: ${attachment.filename}`}
        >
          Il browser non supporta la riproduzione di questo video.
        </video>
        <button
          type="button"
          onClick={() => void downloadAttachment()}
          className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-[10px] underline-offset-2 hover:underline"
          title={`Scarica ${attachment.filename}`}
          aria-label={`Scarica ${attachment.filename}`}
        >
          <span className="truncate">{attachment.filename}</span>
          <span className="flex shrink-0 items-center gap-1 opacity-70">
            {formatFileSize(attachment.file_size)}
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </button>
      </div>
    )
  }

  return null
}

// Re-export type helper for callers that need Id
export type PrescriptionId = Id<'prescriptions'>
